import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { AccountListRow } from '@/components/account/AccountListRow';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Button, ButtonText } from '@/components/ui/button';
import { ScrollView } from '@/components/ui/scroll-view';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useColors } from '@/hooks/useColors';
import { usePollHistory } from '@/features/polls';
import { trackPollResultsViewed } from '@/util/analyticsEvents';
import { formatAnsweredDate } from '@/features/polls/model';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-md' });

/**
 * Every poll the user has answered, newest first, each opening its results.
 * Reached from the Account screen and the web account menu, which only offer
 * it once there is a poll to list, so the empty state is for a user who
 * arrives by URL.
 */
export default function PollHistoryScreen() {
  const { navigate } = useRouter();

  const { bottom } = useSafeAreaInsets();

  const { colors } = useColors();

  const { polls, isLoading, isError, refetch } = usePollHistory();

  let content: React.ReactNode;

  if (isLoading) {
    content = <Spinner size="large" className="py-10" />;
  } else if (isError) {
    content = (
      <VStack className="items-center gap-4 py-10">
        <Text className="text-center text-typography-700">We couldn't load your polls.</Text>

        <Button variant="outline" action="secondary" onPress={() => refetch()}>
          <ButtonText>Try again</ButtonText>
        </Button>
      </VStack>
    );
  } else if (polls.length === 0) {
    content = (
      <Text className="py-10 text-center text-typography-700">
        You haven't answered any polls yet.
      </Text>
    );
  } else {
    content = polls.map((poll) => (
      <AccountListRow
        key={poll.surveyGroupId}
        title={poll.name}
        subtitle={formatAnsweredDate(poll.submittedAt)}
        onPress={() => {
          trackPollResultsViewed({ surveyGroupId: poll.surveyGroupId });

          navigate(ROUTES.ACCOUNT_POLL(poll.surveyGroupId));
        }}
      />
    ));
  }

  return (
    <VStack className={twJoin('flex-1', SCREEN_COLUMN.frame)} style={{ paddingBottom: bottom }}>
      <Header
        contentScrollsUnderHeader
        statusBarBackgroundColor={colors.background[0]}
        title="Your polls"
        isBackButton
        backRoute={ROUTES.ACCOUNT}
      />

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="w-full flex-1"
        contentContainerClassName={twJoin('gap-4 py-6', SCREEN_COLUMN.content)}>
        <Text className="text-typography-700">
          See how other clinicians answered the polls you've taken.
        </Text>

        {content}
      </ScrollView>
    </VStack>
  );
}
